import { Link } from "react-router-dom";
import { Quotes, ArrowRight, Compass, BookOpen, Scales } from "@phosphor-icons/react";
import PageHero from "@/components/site/PageHero";
import ParallaxFrame from "@/components/site/ParallaxFrame";
import { Reveal } from "@/components/motion/Reveal";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/v9xctajy_IMG_0035.jpeg";

const PILLARS = [
  { icon: Compass, title: "Our philosophy", body: "Practice precedes principle. We start with real prosecution histories and work backwards to the doctrine." },
  { icon: BookOpen, title: "Our pedagogy", body: "Small cohorts, case studies-led teaching, weekly live sessions, and 1:1 course review with Venkataramanan." },
  { icon: Scales, title: "Our standard", body: "If a graduate's course wouldn't pass a partner's desk review, we don't issue the certificate. Period." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-navy">
      <PageHero
        testid="about-hero"
        overline="About the School"
        lines={[
          <>A guru-shishya tradition,</>,
          <>
            built for a <em className="italic text-[#C6A87C]">world of patents.</em>
          </>,
        ]}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start py-20 lg:py-28">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg md:text-xl text-[#8A99AA] font-light leading-relaxed max-w-2xl">
                Gurukul of Ramana was founded on a simple frustration: most IP
                education teaches you the law, but not the craft. You can recite
                §112 and §103 and still write a claim that doesn&rsquo;t survive
                examination.
              </p>
              <p className="mt-6 text-lg md:text-xl text-[#8A99AA] font-light leading-relaxed max-w-2xl">
                We teach the craft. Every cohort sits with mentors who are talented in
                Patent Analytics, Patent Drafting, IP Strategy, etc. — and who treat your
                course the way they&rsquo;d treat a junior associate.
              </p>
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.15}>
              <ParallaxFrame
                src={FOUNDER_IMG}
                alt="Venkataramanan Mandakolathur — Founder"
                className="aspect-[4/5]"
                imgClassName="object-top"
                caption="Venkataramanan Mandakolathur · IP Strategist & Prolific Inventor"
              />
            </Reveal>
          </aside>
        </div>

        <div className="border-y border-[#C6A87C]/10 py-20 lg:py-24 grid lg:grid-cols-3 gap-12">
          {PILLARS.map((it, i) => {
            const Icon = it.icon;
            return (
              <Reveal key={it.title} delay={i * 0.12}>
                <Icon size={32} weight="light" className="text-[#C6A87C]" />
                <h3 className="mt-5 font-serif text-2xl lg:text-3xl text-[#F5F4F0]">{it.title}</h3>
                <p className="mt-3 text-[#8A99AA] font-light leading-relaxed">{it.body}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <div className="my-20 lg:my-28 bg-navy-deep border border-[#C6A87C]/15 p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 blueprint-grid-dark opacity-40 pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-1">
                <Quotes size={48} weight="fill" className="text-[#C6A87C]" />
              </div>
              <blockquote className="lg:col-span-8 font-serif text-3xl lg:text-4xl text-[#F5F4F0] leading-tight">
                &ldquo;An invention well-claimed is half-defended.
                <br />
                An invention well-valued is half-monetised.&rdquo;
              </blockquote>
              <div className="lg:col-span-3 lg:text-right">
                <Link
                  to="/courses"
                  data-testid="about-cta-courses"
                  className="group relative inline-flex items-center gap-2 h-12 px-7 bg-[#C6A87C] text-[#061324] text-xs font-semibold tracking-[0.14em] uppercase overflow-hidden"
                >
                  <span className="absolute inset-0 bg-[#E2C69D] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                  <span className="relative inline-flex items-center gap-2">
                    See programmes <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
